import { CheckCircle, Error, Info, OfflineBolt, Pending } from '@mui/icons-material';
import { Chip, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ActionButton } from '~/components/button';
import { formatDate } from '~/lib/datetime';
import { titleize } from '~/lib/string';
import type { DeviceCommands__DeviceCommand as DeviceCommand } from '../../../../queries/commands.generated';

const commandSchema = z.object({
  command: z.string(),
  arbitrary: z.string().optional(),
});

export const useColumns = (
  handleOpenDialog: (row: DeviceCommand) => void,
): GridColDef<DeviceCommand>[] => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  const getStatusProps = useCallback(
    (status: string) => {
      switch (status) {
        case 'DELIVERED':
          return {
            icon: <CheckCircle />,
            color: 'success' as const,
            label: t('common.command_status.delivered'),
          };
        case 'EXPIRED':
          return {
            icon: <Error />,
            color: 'error' as const,
            label: t('common.command_status.expired'),
          };
        case 'PENDING':
          return {
            icon: <Pending />,
            color: 'primary' as const,
            label: t('common.command_status.pending'),
          };
        case 'REALTIME':
          return {
            icon: <OfflineBolt />,
            color: 'tertiary' as const,
            label: t('common.command_status.realtime'),
          };
        default:
          return {
            icon: <Pending />,
            color: 'primary' as const,
            label: t('common.command_status.pending'),
          };
      }
    },
    [t],
  );

  return useMemo(
    () => [
      {
        field: 'command',
        headerName: t('pages.devices.show.command_history.columns.command'),
        sortable: false,
        renderCell: ({ row }) => {
          const parsedCommand = commandSchema.parse(row.command || { command: 'N/A' });
          const commandKey = parsedCommand.command.toLowerCase().replace(/ /g, '_');
          const translationKey = `common.device_commands.${commandKey}`;

          // Try to get translation, fallback to titleized command if not found
          const translatedCommand = t(translationKey, {
            defaultValue: titleize(parsedCommand.command),
          });
          return translatedCommand;
        },

        flex: 0.3,
        minWidth: 150,
      },
      {
        field: 'createdAt',
        headerName: t('common.labels.created_at'),
        sortable: false,
        renderCell: ({ row }) => {
          return formatDate(row.createdAt);
        },
        flex: 0.3,
        minWidth: 250,
      },
      {
        field: 'expiresAt',
        headerName: t('pages.devices.show.command_history.columns.expires_at'),
        sortable: false,
        renderCell: ({ row }) => (row.status === 'DELIVERED' ? '--' : formatDate(row.expiresAt)),
        flex: 0.3,
        minWidth: 250,
      },
      {
        field: 'status',
        headerName: t('common.labels.status'),
        minWidth: 120,
        resizable: false,
        sortable: false,
        renderCell: ({ row }) => {
          const { icon, color, label } = getStatusProps(row.status);
          return <Chip icon={icon} label={label} color={color} />;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        align: 'right',
        minWidth: isSmallAndUp ? 50 : 0,
        getActions: ({ row }) => {
          return [
            <ActionButton
              key={`view-${row.id}`}
              title={t('common.labels.details')}
              Icon={Info}
              onClick={() => handleOpenDialog(row)}
            />,
          ];
        },
      },
    ],
    [isSmallAndUp, handleOpenDialog, t, getStatusProps],
  );
};
